/**
 * Price — the accepted lines, priced from the shop's own catalog.
 *
 * Two kinds of prompt appear above the table, and neither answers itself:
 * a missing price is asked once and kept, and an exclusion is offered as a line
 * the estimator may add. Hours are typed here because no photograph contains
 * them.
 */

import { useState } from 'react';
import { useSession } from '../app/SessionProvider.js';
import type { Job } from '../app/useJob.js';
import type { CatalogEntry } from '../estimate/build.js';
import type { RateProfile } from '../calc/types.js';

export function Price({ job, profile, onSaveToCatalog, onOpenCatalog }: {
  job: Job;
  /** Rates, so each row can show what its hours are actually worth. */
  profile: RateProfile;
  /**
   * Write a price into the shop's catalog, so the next job already knows it.
   *
   * The prompt has always SAID "the answer is kept — you will not be asked
   * again", and it was not: the price went into this job's state and nowhere
   * else, so the same part asked again on the next vehicle. A promise the
   * product does not keep is worse than no promise.
   */
  onSaveToCatalog?: (entry: CatalogEntry) => Promise<void>;
  /** Open the catalog on this part — "from WHICH row?" is always the next question. */
  onOpenCatalog?: (partName: string) => void;
}) {
  const { t, money, hours } = useSession();
  const locked = Boolean(job.issued);

  // Which line's price is open for editing, and what has been typed into it.
  const [editing, setEditing] = useState<string | null>(null);
  const [typed, setTyped] = useState('');
  const [keep, setKeep] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);
  // Which line is open for renaming / re-typing the part.
  const [editPart, setEditPart] = useState<string | null>(null);
  const [partDraft, setPartDraft] = useState({ description: '', partNumber: '', partType: '' });


  /** The rate this line's hours are charged at. */
  const rateFor = (t: string) =>
    (profile.labourRates as Record<string, number>)[t] ?? 0;

  /**
   * What one row contributes.
   *
   * Parts multiply by quantity; labour does not — two bumper covers cost twice
   * as much, but fitting them does not automatically take twice the hours.
   * Refinish is charged on its own line, so it is not counted here as well.
   */
  const lineTotal = (l: { partPrice: number; quantity: number; hours: number;
                          labourType: string }) =>
    l.partPrice * l.quantity
    + (l.labourType === 'none' ? 0 : l.hours * rateFor(l.labourType));
  /**
   * Apply a price to this job, and — when asked — to the catalog as well.
   *
   * A line id is `zone::part#part`; the price map is keyed on `zone::part`
   * without the suffix. Passing the id straight through would have written a
   * catalog row called "bumper cover#part" and left the line unpriced — the
   * kind of near-miss that looks like the save button not working.
   */
  async function commit(line: { id: string; description: string; partNumber?: string;
                               partType: string }) {
    const v = Number(typed);
    if (!Number.isFinite(v) || v < 0) return;
    const priceKey = line.id.includes('#')
      ? line.id.slice(0, line.id.lastIndexOf('#')) : line.id;
    setSaving(true);
    try {
      job.setPrice(priceKey, v);
      if (keep && onSaveToCatalog) {
        await onSaveToCatalog({
          description: line.description,
          partType: line.partType as CatalogEntry['partType'],
          price: v,
          ...(line.partNumber ? { partNumber: line.partNumber } : {}),
        });
        setSaved(line.id);
      }
      setEditing(null);
      setTyped('');
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="screen">
      <h2>{t('Price')}</h2>
      <p className="muted lede">
        Lines you accepted, priced from your own catalog. Hours are yours — nothing
        here guesses them.
      </p>

      {/* Two abreast once there is room. Each prompt is independent — nothing is
          read in order — so stacking them single-file only pushed the price
          table below the fold and left half the page empty. */}
      <div className="notegrid">
      {job.prompts.map((p) =>
        p.kind === 'missing_price' ? (
          <div className="note" key={p.key}>
            <b>Not in your catalog</b>
            <p>
              <b>{p.partLabel}</b> has no price on file. What do you charge? The answer
              is kept — you will not be asked again.
            </p>
            {/* The honest version. "Not in your catalog" reads as a bug when
                the shop can see the part sitting in their catalog. */}
            {p.nearMisses && p.nearMisses.length > 0 && (
              <div className="nearmiss">
                <b>You do have {p.nearMisses.length === 1 ? 'a row' : 'rows'} called
                  “{p.nearMisses[0]!.description}” — but not for this vehicle:</b>
                <ul>
                  {p.nearMisses.map((m, i) => (
                    <li key={i}>
                      <span className="mono">{money(m.price)}</span> —{' '}
                      {[m.make, m.bodyStyle].filter(Boolean).join(' · ') || 'any vehicle'}
                    </li>
                  ))}
                </ul>
                <p className="muted small">
                  Price it here for this vehicle, or open <b>Catalog</b> and clear the
                  make or body style on one of those rows to make it apply to everything.
                </p>
              </div>
            )}
            {/* Price ALONE was never enough. A part with no hours still cannot
                be charged out - somebody has to fit it and somebody has to paint
                it - so the form that exists to stop the app asking again has to
                collect the times too, or it will ask again for exactly the same
                part on exactly the same next vehicle. */}
            <form
              className="fillin"
              onSubmit={(e) => {
                e.preventDefault();
                const f = e.currentTarget;
                const num = (name: string) => {
                  const el = f.elements.namedItem(name) as HTMLInputElement | null;
                  const v = Number(el?.value);
                  // Blank is NOT zero. Zero says "this takes no time", which is
                  // a different and usually wrong claim.
                  return el && el.value.trim() !== '' && Number.isFinite(v) && v >= 0
                    ? v : undefined;
                };
                const price = num('price');
                if (!(price !== undefined && price > 0)) return;

                const replaceHours = num('replaceHours');
                const refinishHours = num('refinishHours');
                const keepInCatalog =
                  (f.elements.namedItem('keep') as HTMLInputElement | null)?.checked ?? false;

                job.setPrice(p.key, price);
                if (refinishHours !== undefined) job.setRefinish(p.key, refinishHours);

                // Two different intentions, and the shop says which. A one-off
                // price for an odd vehicle should NOT become shop policy, and
                // silently writing it to the catalog made every exception
                // permanent.
                if (keepInCatalog && onSaveToCatalog) {
                  void onSaveToCatalog({
                    description: p.partLabel,
                    partType: 'new_oem',
                    price,
                    replaceHours,
                    refinishHours,
                  }).then(() => setSaved(p.key));
                } else {
                  setSaved(null);
                }
                f.reset();
              }}
            >
              <div className="fillrow">
                <label className="fill">
                  <span>Part price</span>
                  <input name="price" type="number" step="0.01" min="0" placeholder="0.00"
                         required disabled={locked} />
                </label>
                <label className="fill">
                  <span>Fit hours<em> R&amp;R</em></span>
                  <input name="replaceHours" type="number" step="0.1" min="0"
                         placeholder="—" disabled={locked} />
                </label>
                <label className="fill">
                  <span>Refinish hours</span>
                  <input name="refinishHours" type="number" step="0.1" min="0"
                         placeholder="—" disabled={locked} />
                </label>
              </div>
              <label className="keepchk">
                <input name="keep" type="checkbox" defaultChecked disabled={locked} />
                <span>
                  <b>Also keep this in my catalog</b>
                  <em>Untick for a one-off — this estimate only, and you will be
                      asked again next time.</em>
                </span>
              </label>
              <button className="btn primary" type="submit" disabled={locked}>
                Use this price
              </button>
              {saved === p.key && <span className="savedtick">✓ kept in your catalog</span>}
            </form>
          </div>
        ) : (
          <div className="note" key={p.key}>
            <b>This operation excludes</b>
            <p>
              A <b>{p.partLabel}</b> replace does not cover {p.excludes}.
            </p>
            <button className="btn primary" disabled={locked}
                    onClick={() => job.addLine({ ...p.suggests, id: p.key })}>
              Add {p.suggests.description} · {hours(p.suggests.hours)} hr
            </button>
            <button className="btn" disabled={locked}
                    onClick={() => job.dismissPrompt(p.key)}>
              Not this time
            </button>
          </div>
        ),
      )}
      </div>

      <div className="tablewrap">
        <table>
          <thead>
            <tr>
              <th>#</th><th>Op</th><th>Description</th><th>Part no.</th>
              <th className="num">Qty</th>
              <th className="num">Unit price</th>
              <th className="num">Hours</th>
              <th className="num">Refinish hr</th>
              <th className="num">Labour</th>
              <th className="num">Line total</th>
              <th />
              <th>Where from</th>
            </tr>
          </thead>
          <tbody>
            {job.lines.length === 0 ? (
              <tr>
                <td colSpan={12} className="empty">
                  Nothing accepted yet — go to {t('Review')}.
                </td>
              </tr>
            ) : (
              job.lines.map((l, i) => (
                <tr key={l.id}>
                  <td className="mono muted">{i + 1}</td>
                  {/* The same choice as on Review, because an estimator
                      reaches it from either direction: deciding at the photo,
                      or noticing the money here. Switching re-derives the whole
                      line — part or no part, R&R hours or dent hours. */}
                  <td>
                    {!locked && (l.operation === 'replace' || l.operation === 'repair')
                      && l.id.includes('#part') ? (
                      <select className="opsel" value={l.operation}
                              aria-label={`Operation for ${l.description}`}
                              onChange={(e) => job.setOperation(
                                l.id.slice(0, l.id.lastIndexOf('#')),
                                e.target.value)}>
                        <option value="replace">replace</option>
                        <option value="repair">repair</option>
                      </select>
                    ) : l.operation}
                  </td>
                  <td>
                    {editPart === l.id ? (
                      <div className="partedit">
                        <input value={partDraft.description} autoFocus
                               aria-label="Part name"
                               onChange={(e) => setPartDraft(
                                 { ...partDraft, description: e.target.value })} />
                        <input value={partDraft.partNumber} placeholder="part number"
                               aria-label="Part number"
                               onChange={(e) => setPartDraft(
                                 { ...partDraft, partNumber: e.target.value })} />
                        <select value={partDraft.partType} aria-label="Part type"
                                onChange={(e) => setPartDraft(
                                  { ...partDraft, partType: e.target.value })}>
                          {['new_oem', 'aftermarket', 'used', 'existing', 'sublet', 'none']
                            .map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                        <button className="btn small primary" onClick={() => {
                          job.editLine(l.id, {
                            description: partDraft.description.trim() || l.description,
                            partNumber: partDraft.partNumber.trim(),
                            partType: partDraft.partType,
                          });
                          setEditPart(null);
                        }}>Save</button>
                        <button className="btn small"
                                onClick={() => setEditPart(null)}>Cancel</button>
                      </div>
                    ) : l.description}
                  </td>
                  <td className="mono small">{l.partNumber ?? '—'}</td>
                  {/* Quantity multiplies the PART only. Two bumper covers cost
                      twice as much; fitting them does not automatically take
                      twice the hours, and pretending it does would inflate
                      every multi-part line. */}
                  <td className="num">
                    <input type="number" step="1" min="1" value={l.quantity}
                           disabled={locked}
                           onChange={(e) => job.setQuantity(l.id,
                             Math.max(1, Math.round(Number(e.target.value) || 1)))}
                           aria-label={`Quantity for ${l.description}`} />
                  </td>
                  <td className="num pricecell">
                    {editing === l.id ? (
                      <span className="priceedit">
                        <input type="number" step="0.01" min="0" autoFocus
                               value={typed} aria-label={`Price for ${l.description}`}
                               onChange={(e) => setTyped(e.target.value)}
                               onKeyDown={(e) => {
                                 if (e.key === 'Escape') setEditing(null);
                                 if (e.key === 'Enter') void commit(l as never);
                               }} />
                        {/* Default ON: a price typed here is almost always the
                            shop's real price, and the whole point of the
                            catalog is that it stops asking. */}
                        <label className="keep">
                          <input type="checkbox" checked={keep}
                                 onChange={(e) => setKeep(e.target.checked)} />
                          keep in catalog
                        </label>
                        <button className="btn small primary" disabled={saving}
                                onClick={() => void commit(l as never)}>
                          {saving ? '…' : 'Save'}
                        </button>
                        <button className="btn small" onClick={() => setEditing(null)}>
                          Cancel
                        </button>
                      </span>
                    ) : (
                      <button className="pricebtn" disabled={locked}
                              title="Change this price"
                              onClick={() => {
                                setEditing(l.id);
                                setTyped(l.partPrice ? String(l.partPrice) : '');
                                setSaved(null);
                              }}>
                        {l.partPrice ? money(l.partPrice) : '— set'}
                      </button>
                    )}
                    {saved === l.id && <span className="savedtick">✓ catalog</span>}
                  </td>
                  <td className="num">
                    <input
                      type="number" step="0.1" min="0" value={l.hours}
                      disabled={locked}
                      onChange={(e) => job.setHours(l.id, Number(e.target.value))}
                      aria-label={`Hours for ${l.description}`}
                    />
                  </td>
                  {/* Refinish hours were computed by the engine and paid for in
                      the totals — and there was NOWHERE to type them, so they
                      were always zero and paint materials always £0. A panel
                      that gets painted is most of the paint bill. */}
                  <td className="num">
                    {l.labourType === 'refinish' || l.operation === 'refinish' ? (
                      <span className="muted small">—</span>
                    ) : (
                      <input
                        type="number" step="0.1" min="0"
                        value={job.refinishFor(l.id) || ''}
                        placeholder="0"
                        disabled={locked}
                        onChange={(e) => job.setRefinish(
                          l.id.includes('#') ? l.id.slice(0, l.id.lastIndexOf('#')) : l.id,
                          Number(e.target.value))}
                        aria-label={`Refinish hours for ${l.description}`}
                      />
                    )}
                  </td>
                  {/* What those hours are actually worth, on the row that owns
                      them. Typing 2.5 hours used to change only the panel on
                      the right, which reads as the field doing nothing. */}
                  <td className="num">
                    {l.labourType === 'none' || !l.hours ? (
                      <span className="muted">—</span>
                    ) : (
                      <span title={`${l.hours} hr x ${money(rateFor(l.labourType))} (${
                        l.labourType})`}>
                        {money(l.hours * rateFor(l.labourType))}
                      </span>
                    )}
                  </td>
                  <td className="num"><b>{money(lineTotal(l))}</b></td>
                  {/* Where these figures came from. An estimator defending an
                      invoice needs to know which numbers their own catalog
                      stands behind and which somebody typed in a hurry. */}
                  {/* Modify or strike off. A struck line is not deleted from
                      the record — the proposal underneath is what the
                      acceptance rate is counted from, and removing a line from
                      an estimate must not rewrite what the model said. */}
                  <td className="lineacts">
                    {!locked && (
                      <>
                        <button className="btn small" title="Change the part"
                                onClick={() => {
                                  setEditPart(l.id);
                                  setPartDraft({
                                    description: l.description,
                                    partNumber: l.partNumber ?? '',
                                    partType: l.partType,
                                  });
                                }}>Edit</button>
                        <button className="btn small rm" title="Take this off the estimate"
                                onClick={() => job.removeLine(l.id)}>Remove</button>
                      </>
                    )}
                  </td>
                  <td className="srccell">
                    {l.priceSource === 'catalog' && (onOpenCatalog
                      ? <button className="src cat linky" title="Open the row this came from"
                                onClick={() => onOpenCatalog(l.description)}>catalog ↗</button>
                      : <span className="src cat">catalog</span>)}
                    {l.priceSource === 'typed' && <span className="src typed">you typed</span>}
                    {l.hoursSource === 'catalog' && <span className="src cat">hr catalog</span>}
                    {l.hoursSource === 'typed' && <span className="src typed">hr typed</span>}
                    {!l.priceSource && !l.hoursSource && <span className="src none">—</span>}
                    {l.priceSource === 'none' && l.hoursSource === undefined
                      && <span className="src none">nothing on file</span>}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Said out loud. A line that vanished with no trace reads as data loss,
          and the estimator needs a way back if they struck the wrong one. */}
      {job.removedLines.length > 0 && !locked && (
        <div className="note">
          <b>{job.removedLines.length} line{job.removedLines.length === 1 ? '' : 's'} taken
            off this estimate</b>
          <p>They are not billed and not printed. The proposals behind them are still on
            record, so your acceptance rate is unaffected.</p>
          <div className="btnrow">
            {job.removedLines.map((id) => (
              <button key={id} className="btn small"
                      onClick={() => job.restoreLine(id)}>
                Put back “{id.split('::').pop()?.split('#')[0] ?? id}”
              </button>
            ))}
          </div>
        </div>
      )}

      {locked && (
        <div className="note warn">
          <b>Issued</b>
          This version is frozen. Revise it and it becomes version 2, which needs its
          own approval.
        </div>
      )}
    </section>
  );
}
